import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiKey } from "../../config/config";
import axios from "axios";
import { toast } from "react-toastify";
import { darkenHexColor, languageHelper } from "../../config/helperFunctions";

function Login() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [logo, setLogo] = useState(null);

  const handleInput = (e) =>
    setCredentials((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);

    axios
      .post(apiKey + "/api/rms/admin/login", credentials, {
        headers: { "Content-Type": "application/json" },
      })
      .then((res) => {
        // persist optionally
        localStorage.setItem("rms-admin", JSON.stringify(res.data.data));
        toast.success(res.data.message, {
          position: "bottom-right",
          autoClose: 5000,
          theme: "colored",
        });
        navigate("/POS/main");
      })
      .catch((err) => {
        console.error(err);
        toast.error(
          err?.response?.data?.message || "Login failed. Please try again.",
          {
            position: "bottom-right",
            autoClose: 5000,
            theme: "colored",
          }
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (localStorage.getItem("rms-admin")) {
      navigate("/POS/main");
    }
  }, [navigate]);

  // theme colors
  useEffect(() => {
    axios
      .get(`${apiKey}/api/rms/customer/settings`)
      .then((res) => {
        let colors = res.data.filter((item) => item.type === "color_theme");
        colors = JSON.parse(colors[0].value);

        document.documentElement.style.setProperty(
          "--color-d-bg",
          colors.color_1
        );
        document.documentElement.style.setProperty(
          "--color-d-pop-btn",
          colors.color_2
        );
        document.documentElement.style.setProperty(
          "--color-d-bg-text",
          colors.color_3
        );
        document.documentElement.style.setProperty(
          "--color-d-btn-text",
          colors.color_4
        );
        document.documentElement.style.setProperty(
          "--color-d-cat-btn",
          colors.color_5
        );
        document.documentElement.style.setProperty(
          "--color-d-bg-dark",
          darkenHexColor(colors.color_5, 20)
        );

        let logo = res.data.find((item) => item.type === "logo");
        logo = JSON.parse(logo.value);

        setLogo(logo.logo_image_url);
      })
      .catch((err) => console.log(err))
      .finally(() => setPageLoading(false));
  }, []);

  if (pageLoading) {
    return (
      <div className="absolute inset-0 grid place-items-center">
        <img src="/assets/gif/dash2.gif" alt="Loading" />
      </div>
    );
  }

  return (
    <main
      className="relative min-h-screen w-full grid place-items-center px-4"
      style={{
        background:
          "linear-gradient(rgba(255,255,255,0.55), rgba(255,255,255,0.55)), url('/assets/login/login_bg.jpg') center/cover no-repeat",
      }}
    >
      {/* floating dots */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-[6%] top-[12%] w-3 h-3 bg-white/70 rounded-full" />
        <div className="absolute left-[16%] top-[34%] w-6 h-6 bg-white/70 rounded-full" />
        <div className="absolute right-[10%] top-[14%] w-8 h-8 bg-white/60 rounded-full" />
        <div className="absolute right-[18%] top-[40%] w-2.5 h-2.5 bg-white/70 rounded-full" />
        <div className="absolute left-[8%] bottom-[18%] w-10 h-10 bg-white/65 rounded-full" />
        <div className="absolute right-[6%] bottom-[22%] w-4 h-4 bg-white/70 rounded-full" />
      </div>

      {/* left illustration */}
      <div className="hidden md:flex absolute left-6 xl:left-16 bottom-10 items-center justify-center">
        <div
          className="relative w-[260px] h-[260px] md:w-[300px] md:h-[300px] 
                  rounded-full bg-white/70 backdrop-blur-sm shadow-sm overflow-visible"
        >
          <img
            src="/assets/login/login1.png"
            alt="Left illustration"
            className="absolute w-[120%] max-w-none -bottom-8 right-0 object-contain drop-shadow"
          />
        </div>
      </div>

      {/* right illustration */}
      <div className="hidden md:flex absolute right-6 xl:right-16 top-20 items-center justify-center">
        <div className="relative w-[240px] h-[240px] md:w-[280px] md:h-[280px] rounded-full bg-white/70 backdrop-blur-sm shadow-sm grid place-items-center">
          <img
            src="/assets/login/login2.png"
            alt="Right illustration"
            className="absolute w-[130%] max-w-none right-0 object-contain drop-shadow"
          />
        </div>
      </div>

      {/* center card */}
      <div className="relative bg-white/95 rounded-2xl shadow-lg w-full max-w-[560px] z-10">
        <div className="px-10 pt-10 pb-4">
          <div className="flex justify-center mb-6">
            {/* sample logo (replace with /assets/logo/imdady_logo.png if preferred) */}
            <img
              className="h-10"
              src={logo || "/assets/logo/imdady_logo.png"}
              alt="Imdady"
            />
          </div>

          <h1 className="text-center text-[#222] font-semibold text-2xl md:text-[26px]">
            {languageHelper("Welcome back")}
          </h1>
          <p className="text-center text-[#8C8C8C] text-sm mt-1">
            {languageHelper("Welcome back! Please enter your details")}
          </p>

          <form onSubmit={handleLogin} className="mt-6">
            <label className="block text-xs text-[#6e6e6e] mb-1">
              {languageHelper("Email")}
            </label>
            <input
              className="w-full px-4 py-2.5 rounded-md bg-[#F6F7FC] border border-[#e6e6e6] outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
              type="email"
              placeholder={languageHelper("Enter your email")}
              name="email"
              onChange={handleInput}
              value={credentials.email}
              required
            />

            <label className="block text-xs text-[#6e6e6e] mt-4 mb-1">
              {languageHelper("Password")}
            </label>
            <input
              className="w-full px-4 py-2.5 rounded-md bg-[#F6F7FC] border border-[#e6e6e6] outline-none focus:ring-2 focus:ring-[#7C3AED]/30"
              type="password"
              placeholder="••••••••"
              name="password"
              onChange={handleInput}
              value={credentials.password}
              required
            />

            <button
              type="submit"
              className="mt-5 w-full rounded-md bg-d-cat-btn text-white py-2.5 font-medium relative disabled:opacity-70"
              disabled={loading}
            >
              {loading && (
                <span className="absolute inset-0 grid place-items-center">
                  <img
                    className="w-5 h-5"
                    src="https://i.gifer.com/ZKZx.gif"
                    alt="Loading"
                  />
                </span>
              )}
              <span className={loading ? "opacity-0" : ""}>
                {languageHelper("login")}
              </span>
            </button>

            {/* <div className="mt-5 flex items-center gap-3">
              <div className="h-px bg-[#e6e6e6] w-full" />
              <span className="text-xs text-[#999]">or</span>
              <div className="h-px bg-[#e6e6e6] w-full" />
            </div> */}
          </form>
        </div>
      </div>
    </main>
  );
}

export default Login;
